import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { changelogVersions } from './changelogContent.ts';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface ChangelogDialogProps {
  open: boolean;
  onClose: () => void;
}

const ChangelogDialog: React.FC<ChangelogDialogProps> = ({ open, onClose }) => {
  const [selectedVersion, setSelectedVersion] = useState(changelogVersions[0].version);
  const [isVersionsOpen, setIsVersionsOpen] = useState(false);

  const currentChangelog = changelogVersions.find(v => v.version === selectedVersion) || changelogVersions[0];

  const handleVersionSelect = (version: string) => {
    setSelectedVersion(version);
    setIsVersionsOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>What's New</DialogTitle>
          <DialogDescription>
            Check out the latest updates and improvements to Naptune.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-1 gap-4 min-h-0 mt-4 flex-col md:flex-row">
          {/* Mobile version selector */}
          <div className="md:hidden">
            <button
              onClick={() => setIsVersionsOpen(!isVersionsOpen)}
              className="w-full flex items-center justify-between px-3 py-2 border border-border rounded-md bg-background hover:bg-accent transition-colors"
            >
              <div>
                <div className="text-sm font-medium">v{currentChangelog.version}</div>
                <div className="text-xs text-muted-foreground">{currentChangelog.date}</div>
              </div>
              <div className={`transition-transform duration-200 ${isVersionsOpen ? 'rotate-180' : 'rotate-0'}`}>
                <ChevronDown className="h-4 w-4" />
              </div>
            </button>

            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                isVersionsOpen ? 'max-h-96 opacity-100 mt-2' : 'max-h-0 opacity-0 mt-0'
              }`}
            >
              <div className="space-y-1 border border-border rounded-md p-2 bg-background">
                {changelogVersions.map((version) => (
                  <button
                    key={version.version}
                    onClick={() => handleVersionSelect(version.version)}
                    className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                      selectedVersion === version.version
                        ? 'bg-primary text-primary-foreground font-medium'
                        : 'hover:bg-accent hover:text-accent-foreground'
                    }`}
                  >
                    <div className="text-sm font-medium">v{version.version}</div>
                    <div className="text-xs opacity-70">{version.date}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Desktop sidebar with version tabs */}
          <div className="hidden md:block w-48 flex-shrink-0 border-r border-border pr-4 overflow-y-auto">
            <div className="space-y-1">
              {changelogVersions.map((version) => (
                <button
                  key={version.version}
                  onClick={() => setSelectedVersion(version.version)}
                  className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                    selectedVersion === version.version
                      ? 'bg-primary text-primary-foreground font-medium'
                      : 'hover:bg-accent hover:text-accent-foreground'
                  }`}
                >
                  <div className="text-sm font-medium">v{version.version}</div>
                  <div className="text-xs opacity-70">{version.date}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Content area */}
          <div className="flex-1 overflow-y-auto pr-2">
            <div className="mb-4 pb-4 border-b border-border hidden md:block">
              <div className="text-sm text-muted-foreground">
                Version {currentChangelog.version} - {currentChangelog.date}
              </div>
            </div>

            <div className="space-y-4 text-sm">
              <ReactMarkdown
                components={{
                  // eslint-disable-next-line jsx-a11y/heading-has-content
                  h1: ({ node, ...props }) => <h1 className="text-2xl font-bold mt-6 mb-4 first:mt-0" {...props} />,
                  // eslint-disable-next-line jsx-a11y/heading-has-content
                  h2: ({ node, ...props }) => <h2 className="text-xl font-semibold mt-5 mb-3" {...props} />,
                  // eslint-disable-next-line jsx-a11y/heading-has-content
                  h3: ({ node, ...props }) => <h3 className="text-lg font-medium mt-4 mb-2" {...props} />,
                  p: ({ node, ...props }) => <p className="mb-4 leading-relaxed" {...props} />,
                  ul: ({ node, ...props }) => <ul className="list-disc list-inside mb-4 space-y-2 ml-4" {...props} />,
                  ol: ({ node, ...props }) => <ol className="list-decimal list-inside mb-4 space-y-2 ml-4" {...props} />,
                  li: ({ node, ...props }) => <li className="mb-1" {...props} />,
                  hr: ({ node, ...props }) => <hr className="my-6 border-border" {...props} />,
                  em: ({ node, ...props }) => <em className="italic" {...props} />,
                  strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
                }}
              >
                {currentChangelog.content}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ChangelogDialog;
